<script setup lang="ts">
// Vue
import { computed, ref, onMounted, watch } from "vue";

// Dependencies
import { v4 as uuidv4 } from "uuid";

// Components
import InputLabel from "./InputLabel.vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import InfoIcon from "@/features/Tooltip/InfoIcon.vue";

const props = defineProps<{
  modelValue?: number | string | null;
  value?: number | string | null;
  label?: string;
  placeholder?: string;
  color: "primary";
  autoComplete?: boolean;
  errorMsg?: string;
  negative?: boolean;
  positive?: boolean;
  noDecimals?: boolean;
  max?: number;
  disabled?: boolean;
  length?: number;
  unit?: string;
  isConnectedRight?: boolean;
  size?: string;
  maxDecimals?: number;
  required?: boolean;
  alignRight?: boolean;
  tooltip?: string;
  markdownPath?: string;
  isLoading?: boolean;
  errorTooltip?: string;
  /** Accessible name for an input whose visible label lives outside this component, such as a table column header. */
  ariaLabel?: string;
  /**
   * Keeps the error message's 20px band under the control whether or not there is a message in it, so a
   * field that starts erroring does not displace itself or anything aligned to it. The band matches
   * `TextInputNew`'s to the pixel — a row that mixes the two only stays aligned if both reserve the same
   * height. Opt-in: on by default it would put 20px of air under every number field in the app.
   */
  reserveErrorSpace?: boolean;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: number | null): void;
  (e: "update:value", value: number | null): void;
}>();

// modelValue is the primary v-model prop; value is kept as a backwards-compatible alias
const effectiveValue = computed(() => (props.modelValue !== undefined ? props.modelValue : props.value));

const localValue = ref<string>("");
const localId = ref<string>(uuidv4());
const inputRef = ref<HTMLInputElement | null>(null);

onMounted(() => {
  if (effectiveValue.value == null) {
    localValue.value = "";
    return;
  }
  localValue.value = String(effectiveValue.value);
  if (props.maxDecimals !== undefined && /\./.test(localValue.value)) {
    const parts = localValue.value.split(".");
    if (parts.length === 2 && parts[1].length > props.maxDecimals) {
      localValue.value = `${parts[0]}.${parts[1].substring(0, props.maxDecimals)}`;
    }
  }
});

const inputChanged = () => {
  if (localValue.value === undefined) return;

  removeStringAndRemoveDuplicatingSymbols();

  // Split the number into base and exponent parts for scientific notation
  const [base, exponent] = localValue.value.split(/[eE]/);

  //if has to be positive
  if (props.positive && /-/.test(base)) localValue.value = localValue.value.replace(/-/g, "");

  // if has to be negative
  if (props.negative && !/-/.test(base)) localValue.value = "";

  // if no decimals allowed
  if (props.noDecimals && /\./.test(base)) {
    localValue.value = localValue.value.replace(/\./g, "");
  }

  // Handle maximum limit if specified
  if (props.max && Number(localValue.value) > props.max) {
    localValue.value = String(props.max);
  }

  // Handle maximum length if specified
  if (props.length && localValue.value.length > props.length) {
    localValue.value = localValue.value.substring(0, localValue.value.length - 1);
  }

  // Handle maximum decimals if specified
  if (props.maxDecimals !== undefined && /\./.test(base)) {
    const parts = base.split(".");
    if (parts.length === 2 && parts[1].length > props.maxDecimals) {
      localValue.value = `${parts[0]}.${parts[1].substring(0, props.maxDecimals)}` + (exponent ? `e${exponent}` : "");
    }
  }

  if (localValue.value === "" || Number.isNaN(+localValue.value)) {
    emits("update:modelValue", null);
    emits("update:value", null);
    return;
  }

  emits("update:modelValue", Number(localValue.value));
  emits("update:value", Number(localValue.value));
};

const removeStringAndRemoveDuplicatingSymbols = () => {
  // Remove leading commas and dots
  if (/^[.,]/.test(localValue.value)) {
    localValue.value = localValue.value.replace(/^[.,]/, "");
  }

  // Replace commas with dots
  if (/,/.test(localValue.value)) {
    localValue.value = localValue.value.replace(/,/g, ".");
  }

  // Update the regex to allow 'E' or 'e' for scientific notation
  if (/[^0-9eE.-]/.test(localValue.value)) {
    localValue.value = localValue.value.replace(/[^0-9eE.-]/g, "");
  }

  // Remove extra dots (shouldn't have more than one dot before 'E' or 'e')
  if ((localValue.value.match(/\./g) || []).length > 1) {
    const parts = localValue.value.split(/[eE]/);
    const baseParts = parts[0].split(".");
    parts[0] = baseParts[0] + "." + baseParts.slice(1).join("");
    localValue.value = parts.join("e");
  }

  // Remove extra dashes (should only be at the start or after an 'e'/'E')
  const dashCount = localValue.value.split("-").length - 1;
  if (dashCount > 2) {
    localValue.value = localValue.value.replace(/-/g, "");
  } else if (dashCount === 2) {
    // Both dashes must be in valid positions
    if (!localValue.value.startsWith("-") && !/e-/.test(localValue.value) && !/E-/.test(localValue.value)) {
      localValue.value = localValue.value.replace(/-/g, "");
    }
  } else if (dashCount === 1) {
    // Single dash must be in a valid position
    if (!localValue.value.startsWith("-") && !/e-/.test(localValue.value) && !/E-/.test(localValue.value)) {
      localValue.value = localValue.value.replace(/-/g, "");
    }
  }

  // Remove extra 'E's or 'e's (shouldn't have more than one 'E' or 'e')
  if (localValue.value.split(/[eE]/).length > 2) {
    const parts = localValue.value.split(/[eE]/);
    localValue.value = parts[0] + "e" + parts[1];
  }
};

const colorClass = computed(() => {
  if (props.color === "primary") return "focus:border-app-blue-600";
  return undefined;
});

watch(
  () => effectiveValue.value,
  (newVal) => {
    if (inputRef.value && document.activeElement === inputRef.value) return;
    localValue.value = newVal == null ? "" : String(newVal);
  }
);
</script>

<template>
  <div class="flex w-full flex-col">
    <InputLabel
      :id="localId"
      :label="label"
      :required="required"
      :tooltip="tooltip"
      :markdown-path="markdownPath" />

    <div
      :class="{ relative: unit || isLoading || errorTooltip }"
      class="w-full">
      <input
        :id="localId ? localId : `input-${localId}`"
        ref="inputRef"
        v-model="localValue"
        type="text"
        :placeholder="placeholder"
        :autocomplete="autoComplete ? 'on' : 'off'"
        :disabled="disabled"
        :aria-label="ariaLabel"
        :aria-required="required ? true : undefined"
        :aria-invalid="errorMsg || errorTooltip ? true : undefined"
        :aria-busy="isLoading ? true : undefined"
        :aria-describedby="errorMsg ? `${localId}-error` : undefined"
        class="focus-input rounded-8 h-9 border px-3 py-2 text-sm leading-none"
        :class="[
          colorClass,
          errorMsg ? 'border-app-red-500' : 'border-slate-300',
          disabled ? 'text-type-muted cursor-not-allowed bg-slate-200' : 'bg-slate-0',
          isConnectedRight ? 'rounded-r-none' : '',
          unit ? 'indent-4' : '',
          size ? size : 'w-full',
          alignRight ? 'text-right placeholder:text-left' : '',
          isLoading || errorTooltip ? 'pr-8' : '',
        ]"
        @input="inputChanged" />
      <div
        v-if="unit"
        class="text-text-color absolute top-1/2 left-2 -translate-y-1/2 text-sm font-bold">
        {{ unit }}
      </div>
      <div
        v-if="isLoading"
        class="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
        <LoadingSpinner
          size="sm"
          fill="button" />
      </div>
      <div
        v-else-if="errorTooltip"
        class="absolute top-1/2 right-3 -translate-y-1/2">
        <InfoIcon :tooltip="errorTooltip">
          <font-awesome-icon
            icon="fa-solid fa-circle-exclamation"
            class="icon-sm text-app-red-600" />
        </InfoIcon>
      </div>
    </div>
    <!-- See `TextInputNew` for why the reserved band is a wrapper with the message still conditional
         inside it, and why it is opt-in (RTLCA-5771). The `mt-1` belongs to the band rather than the
         message so that the reserved height matches `TextInputNew`'s exactly; the unreserved message
         keeps sitting flush under the control, as it always has, everywhere that does not opt in. -->
    <div
      v-if="reserveErrorSpace"
      class="mt-1 min-h-4 text-xs">
      <span
        v-if="errorMsg"
        :id="`${localId}-error`"
        class="text-app-red-700 text-xs"
        >{{ errorMsg }}</span
      >
    </div>
    <span
      v-else-if="errorMsg"
      :id="`${localId}-error`"
      class="text-app-red-700 text-xs"
      >{{ errorMsg }}</span
    >
  </div>
</template>
